import { useState, useEffect, useRef } from "react";
import type { EnrichedProject } from "../types/index.js";
import ProjectSettingsPanel from "./ProjectSettingsPanel.js";
import { filterProjects } from "../App.js";

interface ProjectSwitcherProps {
	projects: EnrichedProject[];
	selectedProjectId: string;
	onSelect: (id: string | null) => void;
	/** Called after any settings action completes, to trigger a project list refresh. */
	onSettingsAction?: () => void;
	/** ISO timestamp of the last successful ingestion, passed to the settings panel. */
	lastIngestedAt?: string | null;
	/** Whether the logged-in user is an admin. */
	isAdmin?: boolean;
	/** Called immediately when a sync is initiated, before the POST completes. */
	onSyncStarting?: () => void;
	/** Called when a sync initiation fails (POST returned an error or threw). */
	onSyncFailed?: () => void;
}

/**
 * Compact project switcher for the sidebar.
 *
 * Displays the currently selected project name as a trigger button. Clicking
 * opens an absolute-positioned dropdown listing all projects. Selecting a project
 * calls onSelect and closes the dropdown.
 *
 * The dropdown contains a search input at the top that filters projects by name.
 * Escape key behaviour is two-step: if the query is non-empty, clears the query
 * first; if the query is already empty, closes the dropdown and returns focus.
 * Click-outside also closes the dropdown and clears the query.
 *
 * When the selected project has a linked WorkDrive folder, a cog icon is shown
 * to the right of the project name trigger, opening the ProjectSettingsPanel.
 */
export default function ProjectSwitcher({ projects, selectedProjectId, onSelect, onSettingsAction, lastIngestedAt = null, isAdmin = false, onSyncStarting, onSyncFailed }: ProjectSwitcherProps) {
	const [open, setOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [settingsOpen, setSettingsOpen] = useState(false);
	const [settingsAnchorRect, setSettingsAnchorRect] = useState<DOMRect | null>(null);

	const containerRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const searchInputRef = useRef<HTMLInputElement>(null);
	const cogRef = useRef<HTMLButtonElement>(null);

	const selectedProject = projects.find((p) => p.id === selectedProjectId);
	const filteredProjects = filterProjects(projects, searchQuery);

	/** Closes the dropdown and resets the search query. */
	function closeDropdown(): void {
		setOpen(false);
		setSearchQuery("");
	}

	// Auto-focus the search input when the dropdown opens
	useEffect(() => {
		if (open) {
			searchInputRef.current?.focus();
		}
	}, [open]);

	// Close dropdown on click outside
	useEffect(() => {
		if (!open) return;

		function handleMouseDown(event: MouseEvent): void {
			if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
				closeDropdown();
			}
		}

		document.addEventListener("mousedown", handleMouseDown);
		return () => {
			document.removeEventListener("mousedown", handleMouseDown);
		};
	}, [open]);

	// Container-level Escape handler — only fires when query is already empty
	// (non-empty query case is handled by the search input's own onKeyDown)
	function handleContainerKeyDown(event: React.KeyboardEvent): void {
		if (event.key === "Escape" && open) {
			// If query is non-empty the input handler stopped propagation — so here
			// the query is always empty; close the dropdown.
			closeDropdown();
			triggerRef.current?.focus();
		}
	}

	/** Handles Escape on the search input with two-step behaviour. */
	function handleSearchKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
		if (event.key === "Escape") {
			if (searchQuery !== "") {
				// First Escape: clear query, keep dropdown open
				event.stopPropagation();
				setSearchQuery("");
			}
			// If query is already empty, let the event bubble to the container handler
		}
	}

	function handleOptionClick(id: string): void {
		onSelect(id);
		closeDropdown();
	}

	/** Opens the settings panel anchored to the cog button. */
	function handleCogClick(event: React.MouseEvent<HTMLButtonElement>): void {
		event.stopPropagation();
		if (settingsOpen) {
			setSettingsOpen(false);
			setSettingsAnchorRect(null);
		} else {
			const rect = event.currentTarget.getBoundingClientRect();
			setSettingsAnchorRect(rect);
			setSettingsOpen(true);
			// Close the dropdown if it's open
			setOpen(false);
		}
	}

	function closeSettings(): void {
		setSettingsOpen(false);
		setSettingsAnchorRect(null);
		cogRef.current?.focus();
	}

	function handleSettingsAction(): void {
		closeSettings();
		onSettingsAction?.();
	}

	return (
		<div
			className="project-switcher-container"
			ref={containerRef}
			onKeyDown={handleContainerKeyDown}
		>
			<div className="project-switcher-row">
				<button
					ref={triggerRef}
					type="button"
					className="project-switcher project-switcher-trigger"
					aria-expanded={open}
					aria-haspopup="listbox"
					aria-label={`Current project: ${selectedProject?.name ?? "Unknown"}. Click to switch.`}
					onClick={() => { if (open) { closeDropdown(); } else { setOpen(true); } }}
				>
					{/* Name is hidden when dropdown is open — the search input serves as the focus point */}
					{!open && (
						<span className="project-switcher-name">
							{selectedProject?.name ?? "Unknown project"}
						</span>
					)}
					<span className="project-switcher-chevron" aria-hidden="true">
						{open ? "▲" : "▼"}
					</span>
				</button>

				{/* Cog button -- shown whenever a project is selected; the settings panel handles the no-folder state gracefully */}
				{selectedProject && (
					<button
						ref={cogRef}
						type="button"
						className={`project-settings-btn project-settings-btn--switcher${settingsOpen ? " project-settings-btn--active" : ""}`}
						title="Project settings"
						aria-label={`Settings for ${selectedProject.name}`}
						onClick={handleCogClick}
					>
						<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
							<circle cx="12" cy="12" r="3"/>
							<path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
						</svg>
					</button>
				)}
			</div>

			{open && (
				<div className="project-list-dropdown-wrapper project-switcher-dropdown">
					<div className="project-search project-search--dropdown" role="search" aria-label="Search projects">
						<input
							ref={searchInputRef}
							className={`project-search-input project-switcher-search${searchQuery.length > 0 ? " project-search-input--has-value" : ""}`}
							type="search"
							placeholder="Search projects..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							onKeyDown={handleSearchKeyDown}
							aria-label="Search projects"
							autoComplete="off"
						/>
						{searchQuery.length > 0 && (
							<button
								className="project-search-clear"
								type="button"
								aria-label="Clear search"
								onClick={() => {
								setSearchQuery("");
								searchInputRef.current?.focus();
							}}
							>
								&#x2715;
							</button>
						)}
					</div>
					<ul
						className="project-list-dropdown"
						role="listbox"
						aria-label="Projects"
					>
						{filteredProjects.length === 0 ? (
							<li className="project-switcher-empty" role="none">
								No matching projects
							</li>
						) : (
							filteredProjects.map((project) => {
								const isActive = project.id === selectedProjectId;
								return (
									<li key={project.id} role="none">
										<button
											type="button"
											role="option"
											aria-selected={isActive}
											className={`project-switcher-option${isActive ? " project-switcher-option--active" : ""}`}
											onClick={() => handleOptionClick(project.id)}
										>
											<span className="project-switcher-option-name">{project.name}</span>
											{project.ingestionBadge !== "none" && (
												<span
													className={`project-badge project-badge--${project.ingestionBadge}`}
													aria-label={project.ingestionBadge === "ready" ? "Documents indexed" : "Indexing in progress"}
												/>
											)}
										</button>
									</li>
								);
							})
						)}
					</ul>
				</div>
			)}

			{/* Project settings panel -- rendered via portal when the cog button is clicked */}
			{settingsOpen && selectedProject && settingsAnchorRect !== null && (
				<ProjectSettingsPanel
					project={selectedProject}
					anchorRect={settingsAnchorRect}
					onClose={closeSettings}
					onSyncTriggered={handleSettingsAction}
					onFolderChanged={handleSettingsAction}
					onFolderUnlinked={handleSettingsAction}
					lastIngestedAt={lastIngestedAt}
					isAdmin={isAdmin}
					onSyncStarting={onSyncStarting}
					onSyncFailed={onSyncFailed}
				/>
			)}
		</div>
	);
}
